import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Grid,
  Select,
  MenuItem,
  Link,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ProfileHeader from "../../../common/components/profile-header/profile-header-and-breadcrumbs";
import { routes } from "../../../constants/routes";
import { useDispatch, useSelector } from "react-redux";
import { getCurrentUserDetails } from "../../../common/routines/user-operations";
import {
  submitSupportRequest,
  getSupportHistory,
  SupportTicket,
} from "../../../api/preneed-section/communication.api";
import { useSetLoading } from "../../../common/hooks/appearance";
import { AlertComponent } from "../../../common/components";
import "./communication.scss";
import dayjs from "dayjs";
import Flags from "react-world-flags";
import { getIconBasedOnFileExt } from "../../../utils/fetchIconForExtension";

const countryCodes = [
  { code: "+1", flag: "US", name: "United States" },
  { code: "+91", flag: "IN", name: "India" },
];

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .min(2, "Name must be at least 2 characters")
    .required("Name is required"),
  contactEmail: Yup.string()
    .trim()
    .required("Email is required")
    .matches(
      /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
      "Please enter a valid email address",
    ),
  contactNumber: Yup.string()
    .trim()
    .test(
      "phone-format",
      "Phone number must be exactly 10 digits",
      (value) => !value || /^\d{10}$/.test(value),
    )
    .nullable(),
  subject: Yup.string()
    .trim()
    .min(1, "Subject is required.")
    .max(50, "Subject must not exceed 50 characters.")
    .required("Subject is required."),
  detailedDescription: Yup.string()
    .trim()
    .min(1, "Description cannot be empty.")
    .max(150, "Description must not exceed 150 characters.")
    .required("Description cannot be empty."),
  attachment: Yup.array()
    .of(
      Yup.mixed()
        .test(
          "fileSize",
          "File size must be 10MB or less",
          (value) =>
            !value || (value instanceof File && value.size <= 10 * 1024 * 1024),
        )
        .test(
          "fileFormat",
          "Only PDF, DOC, DOCX, PNG, JPG, and JPEG files are allowed",
          (value) => {
            if (!value) return true;
            if (!(value instanceof File)) return false;
            const validTypes = [
              "application/pdf",
              "application/msword",
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
              "image/png",
              "image/jpeg",
            ];
            const validExts = ["pdf", "doc", "docx", "png", "jpg", "jpeg"];
            const ext = value.name.split(".").pop()?.toLowerCase() || "";
            return validTypes.includes(value.type) || validExts.includes(ext);
          },
        ),
    )
    .nullable(),
});

const Communication: React.FC = () => {
  const navigate = useNavigate();
  const preNeedDetails = useSelector(
    (state: any) => state.session.preNeedDetails,
  );
  const [loading, setLoading] = useState(false);
  const [supportHistory, setSupportHistory] = useState<SupportTicket[]>([]);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [submittedTicketId, setSubmittedTicketId] = useState<string | null>(
    null,
  );

  const breadcrumbLink = [
    {
      label: "AFP Tech Support",
      link: routes.PRENEED_COMMUNICATION,
      handleClick: () => {},
      disablePointerEvents: true,
    },
  ];

  const preneedUser = getCurrentUserDetails("preNeed");
  const preneedName =
    preneedUser?.user?.first_name && preneedUser?.user?.last_name
      ? `${preneedUser.user.first_name} ${preneedUser.user.last_name}`
      : preneedUser?.user?.first_name || preneedUser?.user?.name || "";
  const preneedEmail = preneedUser?.user?.email || "";
  const preneedPhone = preneedUser?.user?.mobileNumber || "";

  const formik = useFormik({
    initialValues: {
      name: "",
      contactEmail: "",
      contactNumber: preneedPhone || "",
      countryCode: "+1",
      subject: "",
      detailedDescription: "",
      attachment: [] as File[],
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (
      values,
      { resetForm, setSubmitting, setTouched, setErrors },
    ) => {
      setTouched({
        name: true,
        contactEmail: true,
        subject: true,
        detailedDescription: true,
      });

      const errors = await formik.validateForm();

      if (Object.keys(errors).length > 0) {
        setErrors(errors);
        setSubmitting(false);
        return;
      }

      try {
        setLoading(true);
        const formData = new FormData();
        formData.append("name", values.name.trim());
        formData.append("contactEmail", values.contactEmail.trim());
        if (values.contactNumber && values.contactNumber.trim()) {
          const fullContactNumber = `${values.countryCode || "+1"}${values.contactNumber.trim()}`;
          formData.append("contactNumber", fullContactNumber);
        }
        formData.append("subject", values.subject.trim());
        formData.append(
          "detailedDescription",
          values.detailedDescription.trim(),
        );
        if (values.attachment && values.attachment.length > 0) {
          values.attachment.forEach((file) => {
            formData.append("attachments", file);
          });
        }

        const response = await submitSupportRequest(formData);

        if (response?.data?.success) {
          let ticketId =
            response.data.data?.ticketId ||
            response.data.data?.ticket_id ||
            response.data.data?.id ||
            response.data.ticketId ||
            response.data.ticket_id ||
            response.data.id ||
            null;

          if (!ticketId) {
            try {
              const historyResponse = await getSupportHistory();
              if (historyResponse?.data?.success) {
                const tickets = historyResponse.data.data?.data || [];
                if (tickets.length > 0) {
                  ticketId =
                    tickets[0]?.ticket_id ||
                    tickets[0]?.ticketId ||
                    tickets[0]?.id ||
                    null;
                }
              }
            } catch (historyError) {
              console.error(
                "Error fetching ticket ID from history:",
                historyError,
              );
            }
          }

          setSubmittedTicketId(ticketId || "N/A");
          setAlertMessage(
            ticketId
              ? `Your support request has been submitted successfully. Ticket ID: ${ticketId}.`
              : "Your support request has been submitted successfully.",
          );
          setAlertType("success");
          setAlertOpen(true);
          resetForm({
            values: {
              name: "",
              contactEmail: "",
              contactNumber: preneedPhone || "",
              countryCode: "+1",
              subject: "",
              detailedDescription: "",
              attachment: [],
            },
          });
          fetchSupportHistory();
        } else {
          throw new Error(
            response?.data?.data?.message ||
              response?.data?.message ||
              "Failed to submit request",
          );
        }
      } catch (error: any) {
        console.error("Support request submission error:", error);
        console.error("Error response:", error?.response?.data);
        const data = error?.response?.data;
        const isValidationError =
          data?.code === "VALIDATION_ERROR" ||
          data?.message === "Input validation failed";
        const errors = data?.errors;
        let errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to submit support request. Please try again.";
        if (isValidationError && Array.isArray(errors) && errors.length > 0) {
          const messages: string[] = [];
          errors.forEach((errObj: any) => {
            if (typeof errObj === "object" && errObj !== null) {
              Object.values(errObj).forEach((val) => {
                if (typeof val === "string") messages.push(val);
              });
            }
          });
          if (messages.length > 0) {
            errorMessage = messages
              .join(" ")
              .replace(/\bcontactEmail\b/g, "Contact Email");
          }
        }
        setAlertMessage(errorMessage);
        setAlertType("error");
        setAlertOpen(true);
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  const fetchSupportHistory = async () => {
    try {
      setLoading(true);
      const response = await getSupportHistory();
      if (response?.data?.success) {
        const tickets = response.data.data?.data?.supportRequests || [];

        const mappedTickets: SupportTicket[] = tickets.map((ticket: any) => ({
          id: ticket.id,
          name: ticket.name,
          ticket_id: ticket.ticket_id,
          subject: ticket.subject,
          created_at: ticket.created_at,
          ticketId: ticket.ticket_id,
          raisedBy: ticket.name,
          dateSubmitted: ticket.created_at,
        }));

        const sortedTickets = mappedTickets.sort(
          (a: SupportTicket, b: SupportTicket) => {
            const dateA = new Date(
              a.created_at || a.dateSubmitted || 0,
            ).getTime();
            const dateB = new Date(
              b.created_at || b.dateSubmitted || 0,
            ).getTime();
            return dateB - dateA;
          },
        );
        setSupportHistory(sortedTickets);
      }
    } catch (error) {
      console.error("Error fetching support history:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSupportHistory();
  }, []);

  useSetLoading(loading);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const newFiles = Array.from(files);
      const currentAttachments = formik.values.attachment || [];
      const totalFiles = currentAttachments.length + newFiles.length;

      if (totalFiles > 5) {
        setAlertMessage("Maximum 5 files are allowed");
        setAlertType("error");
        setAlertOpen(true);
        const fileInput = document.getElementById(
          "attachment-input",
        ) as HTMLInputElement;
        if (fileInput) {
          fileInput.value = "";
        }
        return;
      }

      const updatedAttachments = [...currentAttachments, ...newFiles];
      formik.setFieldValue("attachment", updatedAttachments);
      formik.setFieldTouched("attachment", true);
    }
    const fileInput = document.getElementById(
      "attachment-input",
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const removeAttachment = (indexToRemove: number) => {
    const currentAttachments = formik.values.attachment || [];
    const updatedAttachments = currentAttachments.filter(
      (_, index) => index !== indexToRemove,
    );
    formik.setFieldValue("attachment", updatedAttachments);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) {
      return `${bytes} B`;
    } else if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    } else {
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName?.split(".").pop()?.toLowerCase() || "";
    return getIconBasedOnFileExt(ext);
  };

  return (
    <div className="provider-communication">
      <ProfileHeader mode="preNeed" breadcrumbLink={breadcrumbLink} />
      <Container maxWidth="xl" className="communication-container">
        {/* Communication Support Form Section */}
        <Paper elevation={0} className="communication-form-section">
          <Typography variant="h6" className="section-title">
            AFP Tech Support
          </Typography>
          <Typography variant="body2" className="section-subtitle">
            Tell us what you need help with.
          </Typography>
          <Divider className="section-divider" />

          <form onSubmit={formik.handleSubmit} className="support-form">
            <Box className="form-row">
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" className="field-label">
                    Name <span className="required-asterisk">*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    name="name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    helperText={
                      formik.touched.name && formik.errors.name
                        ? String(formik.errors.name)
                        : ""
                    }
                    placeholder="Enter Name"
                    className="form-field"
                    sx={{
                      "& .MuiInputBase-root.Mui-disabled": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="body2" className="field-label">
                    Contact Email <span className="required-asterisk">*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    name="contactEmail"
                    type="email"
                    value={formik.values.contactEmail}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.contactEmail &&
                      Boolean(formik.errors.contactEmail)
                    }
                    helperText={
                      formik.touched.contactEmail && formik.errors.contactEmail
                        ? String(formik.errors.contactEmail)
                        : ""
                    }
                    placeholder="Enter email"
                    className="form-field"
                    sx={{
                      "& .MuiInputBase-root.Mui-disabled": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="body2" className="field-label">
                    Contact Number
                  </Typography>
                  <Box className="phone-input-wrapper">
                    <Box className="phone-input-container-separate">
                      <Select
                        value={formik.values.countryCode}
                        onChange={(e) => {
                          formik.setFieldValue("countryCode", e.target.value);
                        }}
                        className="country-code-select"
                        renderValue={(value) => {
                          const country = countryCodes.find(
                            (c) => c.code === value,
                          );
                          return (
                            <Box display="flex" alignItems="center" gap={1}>
                              {country && (
                                <Flags
                                  code={country.flag}
                                  style={{ width: "16px", height: "12px" }}
                                />
                              )}
                              <span>{value}</span>
                            </Box>
                          );
                        }}
                        sx={{
                          height: "42px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderRadius: "6px",
                            borderColor: "#e0e0e0",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#bdbdbd",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#1976d2",
                          },
                        }}
                      >
                        {countryCodes.map((country) => (
                          <MenuItem key={country.code} value={country.code}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Flags
                                code={country.flag}
                                style={{ width: "16px", height: "12px" }}
                              />
                              <span>{country.code}</span>
                              <span
                                style={{
                                  marginLeft: "8px",
                                  color: "#666",
                                  fontSize: "14px",
                                }}
                              >
                                {country.name}
                              </span>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                      <TextField
                        fullWidth
                        name="contactNumber"
                        value={formik.values.contactNumber}
                        onChange={(e) => {
                          const digitsOnly = e.target.value.replace(/\D/g, "");

                          const limitedDigits = digitsOnly.slice(0, 10);
                          formik.setFieldValue("contactNumber", limitedDigits);
                        }}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.contactNumber &&
                          Boolean(formik.errors.contactNumber)
                        }
                        placeholder="Enter contact number"
                        className="phone-number-input"
                        inputProps={{
                          maxLength: 10,
                        }}
                        helperText=""
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: "42px",
                            borderRadius: "6px",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#e0e0e0",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#bdbdbd",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#1976d2",
                            },
                          },
                        }}
                      />
                    </Box>
                    {formik.touched.contactNumber &&
                      formik.errors.contactNumber && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#d32f2f",
                            fontSize: "0.75rem",
                            marginTop: "4px",
                            marginLeft: "14px",
                          }}
                        >
                          {String(formik.errors.contactNumber)}
                        </Typography>
                      )}
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Box className="form-row">
              <Typography variant="body2" className="field-label">
                Subject <span className="required-asterisk">*</span>
              </Typography>
              <TextField
                fullWidth
                name="subject"
                value={formik.values.subject}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.subject && Boolean(formik.errors.subject)}
                helperText={
                  formik.touched.subject && formik.errors.subject
                    ? String(formik.errors.subject)
                    : ""
                }
                placeholder="Enter subject"
                className="form-field"
                inputProps={{
                  maxLength: 50,
                }}
              />
            </Box>

            <Box className="form-row">
              <Typography variant="body2" className="field-label">
                Detail Description <span className="required-asterisk">*</span>
              </Typography>
              <TextField
                fullWidth
                name="detailedDescription"
                multiline
                rows={4}
                value={formik.values.detailedDescription}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.detailedDescription &&
                  Boolean(formik.errors.detailedDescription)
                }
                helperText={
                  formik.touched.detailedDescription &&
                  formik.errors.detailedDescription
                    ? String(formik.errors.detailedDescription)
                    : ""
                }
                placeholder="Enter Detail Description"
                className="form-field"
                inputProps={{
                  maxLength: 150,
                }}
              />
            </Box>

            <Box className="form-row">
              <input
                id="attachment-input"
                type="file"
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                multiple
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              <Box className="attachment-link-container">
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    document.getElementById("attachment-input")?.click();
                  }}
                  className="add-attachment-link"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    textDecoration: "none",
                    color: "#1976d2",
                    width: "149px",
                    height: "24px",
                    opacity: 1,
                    "&:hover": {
                      textDecoration: "underline",
                    },
                    marginBottom:
                      formik.values.attachment &&
                      formik.values.attachment.length > 0
                        ? "8px"
                        : "0",
                  }}
                >
                  <AttachFileIcon fontSize="small" />
                  Add attachment
                </Link>
                {formik.touched.attachment && formik.errors.attachment && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#d32f2f",
                      fontSize: "0.75rem",
                      marginTop: "4px",
                      display: "block",
                    }}
                  >
                    {(() => {
                      const err = formik.errors.attachment;
                      const msg = Array.isArray(err)
                        ? Array.from(
                            new Set((err as unknown[]).map((e) => String(e))),
                          ).join(", ")
                        : String(err);
                      return msg.replace(/,+\s*$/, "");
                    })()}
                  </Typography>
                )}
                {formik.values.attachment &&
                  formik.values.attachment.length > 0 && (
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={1}
                      sx={{
                        width: "100%",
                        marginTop: "8px",
                        alignItems: "flex-end",
                      }}
                    >
                      {[...formik.values.attachment]
                        .reverse()
                        .map((file, index) => {
                          const maxWidth = Math.max(
                            ...formik.values.attachment.map((f) => {
                              const text = `${f.name} (${formatFileSize(f.size)})`;
                              return text.length * 8 + 100;
                            }),
                          );
                          return (
                            <Box
                              key={index}
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                              sx={{
                                width: `${maxWidth}px`,
                                minWidth: "fit-content",
                              }}
                            >
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={1.5}
                                sx={{
                                  flex: 1,
                                  minWidth: 0,
                                  marginRight: "6px",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    width: "24px",
                                    height: "24px",
                                    flexShrink: 0,
                                  }}
                                >
                                  {getFileIcon(file.name)}
                                </Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "#111827",
                                    fontWeight: 500,
                                    fontSize: "14px",
                                  }}
                                >
                                  {file.name} ({formatFileSize(file.size)})
                                </Typography>
                              </Box>
                              <IconButton
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const originalIndex =
                                    formik.values.attachment.length - 1 - index;
                                  removeAttachment(originalIndex);
                                }}
                                size="small"
                                sx={{
                                  padding: "4px",
                                  color: "#000",
                                  "&:hover": {
                                    color: "#000",
                                    backgroundColor: "transparent",
                                  },
                                  flexShrink: 0,
                                }}
                                type="button"
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          );
                        })}
                    </Box>
                  )}
              </Box>
            </Box>

            <Box className="form-actions">
              <Button
                variant="outlined"
                onClick={() => formik.resetForm()}
                className="cancel-button"
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  borderColor: "#cecece",
                  borderWidth: "1px",
                  color: "#212121",
                  backgroundColor: "#FFFFFF",
                  width: "113.53125px",
                  height: "46px",
                  paddingTop: "11px",
                  paddingRight: "25px",
                  paddingBottom: "1px",
                  paddingLeft: "25px",
                  opacity: 1,
                  "&:hover": {
                    borderColor: "#cecece",
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                className="submit-button"
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  backgroundColor: "#EBD28D",
                  color: "#212121",
                  width: "153px",
                  height: "44px",
                  paddingTop: "10px",
                  paddingRight: "32px",
                  paddingLeft: "32px",
                  opacity: 1,
                  "&:hover": {
                    backgroundColor: "#EBD28D",
                    opacity: 0.9,
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#f5f5f5",
                    color: "#999",
                  },
                }}
              >
                Submit
              </Button>
            </Box>
          </form>
        </Paper>

        {/* Latest Support History Section */}
        <Paper elevation={0} className="support-history-section">
          <Typography variant="h5" className="section-title">
            Latest Support History
          </Typography>
          <Typography variant="body2" className="section-subtitle">
            View all your recent support emails.
          </Typography>
          <Divider className="section-divider" />

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className="table-header">Ticket ID</TableCell>
                  <TableCell className="table-header">Subject</TableCell>
                  <TableCell className="table-header">Raised By</TableCell>
                  <TableCell className="table-header">Date Submitted</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {supportHistory.length > 0 ? (
                  supportHistory.map((ticket, index) => (
                    <TableRow
                      key={ticket.id ?? index}
                      className="table-row"
                      onClick={() =>
                        navigate(
                          routes.PRENEED_COMMUNICATION_TICKET_DETAILS.replace(
                            ":id",
                            String(ticket.id),
                          ),
                        )
                      }
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell className="table-cell">
                        {ticket.ticketId || ticket.ticket_id}
                      </TableCell>
                      <TableCell className="table-cell">
                        {ticket.subject}
                      </TableCell>
                      <TableCell className="table-cell">
                        {ticket.raisedBy || ticket.name}
                      </TableCell>
                      <TableCell className="table-cell">
                        {dayjs(
                          ticket.dateSubmitted || ticket.created_at,
                        ).format("MM/DD/YYYY")}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center" className="no-data">
                      No support tickets found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>

      <AlertComponent
        message={alertMessage}
        type={alertType}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </div>
  );
};

export default Communication;
